import {gsap} from "../../../libs/gsap/all.js";
import * as PIXI from "pixi.js";
import 'pixi.js/math-extras';
import {Point} from 'pixi.js';

// Constructor
export class GameD_Choice extends PIXI.Container {

  constructor(x, y, data, fontStyle = "normal", game) {
    super();

    // this is use as a fix for buttons that were created early on in development.
    // Set this to false in your constructor if you do not want to override the pivot point when you press
    // Ignore otherwise
    this.overridePivot = false;
    // ==================================================================
    this.originalPosition = {x: 0, y: 0};
    this.originalPivot = {x: 0, y: 0};

    this.game = game;
    this.data = data;

    this.id = this.data.id;
    this.isSelected = false;

    this.position.set(x, y);

    this.clickSound = new Howl({
      src: [this.game.data.global.sounds.click_sound],
      loop: false,
      autoplay: false,
    });

    // Tag text style
    this.tagTextStyle = new PIXI.TextStyle({
      align: "center",
      fill: "#00156B",
      fontFamily: "Sketchnote Text",
      fontSize: 24,
      wordWrap: true,
      wordWrapWidth: 400,
      fontStyle: fontStyle
    });

    this.build();
  }

  build() {
    this.background = new PIXI.Graphics().roundRect(0, 0, this.game.data.tagsSize.width, this.game.data.tagsSize.height, this.game.data.tagsSize.height / 2).fill(0xFFEB5A).stroke({
      width: 0,
      color: 0xFFFFFF
    });
    this.background.pivot.set(this.background.width / 2, this.background.height / 2);
    this.background.position.set(this.background.width / 2, this.background.height / 2);
    this.addChild(this.background);

    this.tagTextEN = this.game.createInterfaceText("", {...this.tagTextStyle}, "en"); // use a copy of the baseButtonTextStyle so we can update them individually
    this.tagTextFR = this.game.createInterfaceText("", {...this.tagTextStyle}, "fr"); // use a copy of the baseButtonTextStyle so we can update them individually
    this.tagTextEN.anchor.set(0.5); // center in box easier
    this.tagTextFR.anchor.set(0.5); // center in box easier
    this.tagTextEN.position.x = this.width / 2;
    this.tagTextFR.position.x = this.width / 2;
    this.tagTextEN.position.y = this.height / 2;
    this.tagTextFR.position.y = this.height / 2;

    this.addChild(this.tagTextEN);
    this.addChild(this.tagTextFR);

    this.pivot.set(this.width / 2, this.height / 2);

    // Good/Bad marker
    let markerSize = this.background.height / 2;
    let markerPosition = {
      x: this.width - (markerSize / 2),
      y: (this.height / 2) - (markerSize / 2)
    };
    this.goodAnswerIcon = new PIXI.Sprite(this.game.assets["good-answer"]);
    this.goodAnswerIcon.width = markerSize;
    this.goodAnswerIcon.height = markerSize;
    this.goodAnswerIcon.position.x = markerPosition.x;
    this.goodAnswerIcon.position.y = markerPosition.y;
    this.goodAnswerIcon.alpha = 0;
    this.addChild(this.goodAnswerIcon);

    this.badAnswerIcon = new PIXI.Sprite(this.game.assets["bad-answer"]);
    this.badAnswerIcon.width = markerSize;
    this.badAnswerIcon.height = markerSize;
    this.badAnswerIcon.position.x = markerPosition.x;
    this.badAnswerIcon.position.y = markerPosition.y;
    this.badAnswerIcon.alpha = 0;
    this.addChild(this.badAnswerIcon);

    this.originalPosition = new PIXI.Point().copyFrom(this.position); // copy original position to a variable for later
  }

  enable() {
    this.eventMode = "static";
    this.cursor = 'pointer';

    this.removeAllEvents();
    this.on("pointerdown", this.onPress, this);
  }

  disable() {
    this.eventMode = "none";
    this.cursor = 'static';

    this.removeAllEvents();
  }

  setText(data) {
    this.tagTextEN.text = data.en;
    this.tagTextFR.text = data.fr;
  }

  showSelected(state = true) {
    gsap.to(this, {
      pixi: {
        alpha: state ? 1.0 : 0.5,
      },
      duration: 0.25,
      ease: "power1.inOut",
    });

    this.isSelected = state;
  }

  showAnswerMarker(state = true) {
    let targetMarker = state ? this.goodAnswerIcon : this.badAnswerIcon;
    gsap.to(targetMarker, {
      pixi: {
        alpha: 1.0,
      },
      duration: 0.25,
      ease: "power1.inOut",
    });
  }

  hideAnswerMarker() {
    gsap.to([this.goodAnswerIcon, this.badAnswerIcon], {
      pixi: {
        alpha: 0.0,
      },
      duration: 0.25,
      ease: "power1.inOut",
    });
  }

  onPress(e) {
    // We need to offset the container and changed the pivot point when pressing so we can scale from center
    if (this.overridePivot) {
      this.originalPosition = {x: this.position.x, y: this.position.y};
      this.originalPivot = {x: this.pivot.x, y: this.pivot.y};
      this.pivot.set(this.width / 2, this.height / 2)
      this.position.x += this.width / 2;
      this.position.y += this.height / 2;
    }

    gsap.to(this, {
      pixi: {
        scale: 0.95
      },
      duration: 0.15,
      overwrite: "auto"
    });

    if (this.clickSound) {
      this.clickSound.play();
    }

    this.off("pointerdown", this.onPress, this);
    this.on("pointerup", this.onRelease, this);
    this.on("pointerupoutside", this.onRelease, this);
  }

  // Animate back and execute action
  onRelease() {

    this.clicked();

    gsap.to(this, {
      pixi: {
        scale: 1
      },
      ease: "back.out(4)",
      duration: 0.15,
      overwrite: "auto",
      onComplete: () => {
        if (this.overridePivot) {
          this.position = {...this.originalPosition};
          this.pivot = {...this.originalPivot};
        }
        this.on("pointerdown", this.onPress, this);
      }
    });

    this.off("pointerup", this.onRelease, this);
    this.off("pointerupoutside", this.onRelease, this);
  }

  removeAllEvents() {
    this.off("pointerdown", this.onPress, this);
    this.off("pointerup", this.onRelease, this);
    this.off("pointerupoutside", this.onRelease, this);
  }

  clicked() {
    this.emit("selected", this);
  }

  resetState(duration) {

  }

  // Reset the states so we can animate in again and start over
  reset() {
    this.setText({en: "", fr: ""});
    this.isSelected = false;
    this.hideAnswerMarker();
  }


}
